import MapView from '@arcgis/core/views/MapView';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { useEffect, useState } from 'react';
import FeatureLayerView from '@arcgis/core/views/layers/FeatureLayerView';
import * as promiseUtils from '@arcgis/core/core/promiseUtils';
import Graphic from '@arcgis/core/Graphic';
import Color from '@arcgis/core/Color';
import { useGraphicsLayer } from '../GraphicsLayer/useGraphicsLayer';
import { useAsyncEffect } from '@repo/utils';
import Geometry from '@arcgis/core/geometry/Geometry';

type ViewImmediateClickEventHandler = __esri.ViewImmediateClickEventHandler;
// import PopupTemplate from '@arcgis/core/PopupTemplate.js';
// import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
// import PopupTemplateProperties = __esri.PopupTemplateProperties;
//
// const baseUrl = process.env.PUBLIC_URL as string;

type GraphicHit = __esri.GraphicHit;
type GeometryProperties = __esri.GeometryProperties;

interface InternalMapView extends MapView {
  cursor: 'default' | 'pointer';
}

const dottedLineObj = {
  type: 'simple-fill', // autocasts as new SimpleFillSymbol()
  color: new Color('rgba(0,0,0,0)'),
  style: 'solid',
  outline: {
    // autocasts as new SimpleLineSymbol()
    style: 'dash',
    color: 'white',
    width: 3
  },
  listMode: 'hide'
};

/**
 * A React hook that manages single-feature selection for a MapView by clicking on a feature in its FeatureLayer.
 *
 * @param view - The ArcGIS MapView instance — may be null/undefined if the map hasn't loaded yet.
 * @param featureLayer - The FeatureLayer whose features are selectable — the click is tested against this layer only.
 * @param clickCallback - Optional callback invoked with the current selection results when the user clicks elsewhere.
 */
export const useSelectSingleMapFeature = (
  view: InternalMapView | MapView | undefined,
  featureLayer: FeatureLayer | undefined,
  clickCallback?: (graphics: GraphicHit[]) => void
  // options:
  // maybe one for allowMultiple if doing in same hook
  // showPopUpTemplate: boolean - send popUpTemplate object so popup can show on map
) => {
  // different hook for right vs left vs shift-click/ctrl-click
  // make it highlight with dotted line on click, unhighlight on click again
  // see selectedBlockLayer in chemPOC (chemContext)
  // listmode is hide

  const [selectedResult, setSelectedResult] = useState<GraphicHit[]>([]);
  const [layerView, setLayerView] = useState<FeatureLayerView>();

  const { graphicsLayer, addGraphicsLayerToMap, clearGraphicsLayer } = useGraphicsLayer({
    map: view?.map,
    view: view,
    graphicOptions: dottedLineObj,
    addOnLoad: false
  });

  useEffect(() => {
    if (view) {
      const clickHandle = view.on('pointer-down', () => {
        if (clickCallback) clickCallback(selectedResult);
      });
      return () => {
        if (clickHandle) clickHandle.remove();
      };
    }
  }, [selectedResult, view, clickCallback]);

  useAsyncEffect(async () => {
    let viewMoveHandle: IHandle;
    if (featureLayer && view && !layerView) {
      const layerView = await view.whenLayerView(featureLayer);
      setLayerView(layerView);
      const selectSingle = (response: __esri.HitTestResult) => {
        if (response.results.length) {
          const [{ graphic }]:any = response.results as GraphicHit[];
          const match = graphicsLayer?.graphics.getItemAt(0);
          // const match2 = graphicsLayer?.graphics.find((gr) => gr === graphic);
          if (match) {
            clearGraphicsLayer(view?.map as any);
            setSelectedResult([]);
          } else {
            const outlineGraphic = new Graphic({
              geometry: graphic.geometry as any,
              symbol: dottedLineObj as any
            });
            addGraphicsLayerToMap(view?.map as any, outlineGraphic);
            setSelectedResult(response.results as GraphicHit[]);
          }
        } else {
          if (selectedResult.length === 0) {
            clearGraphicsLayer(view?.map as any);
            setSelectedResult([]);
          }
        }
      };
      const eventHandler: ViewImmediateClickEventHandler = (event: __esri.ViewImmediateClickEvent) => {
        view.hitTest(event, { include: featureLayer }).then((response) => {
          if (!event.native.ctrlKey && !event.native.shiftKey) {
            promiseUtils.debounce(selectSingle(response));
          }
        });
      };
      viewMoveHandle = view.on('immediate-click', eventHandler);
    }

    return (async () => viewMoveHandle && viewMoveHandle.remove()) as any;
  }, [featureLayer, view]);

  return { selectedResult };
};


/**
 * Zooms to and highlights a feature in a FeatureLayer by its OBJECTID, loading the feature layer view and drawing a dotted outline on the matching feature.
 *
 * Skips the current highlight if the feature is already selected. Clears zoom result when the feature is not found or when the id changes.
 */
export const useZoomToFeatureById = (
  id: string | undefined,
  view: InternalMapView | MapView | undefined,
  featureLayer: FeatureLayer | undefined,
  map: any
) => {
  const [zoomResult, setZoomResult] = useState<Graphic[]>([]);
  const [layerView, setLayerView] = useState<FeatureLayerView>();

  const { graphicsLayer, addGraphicsLayerToMap, clearGraphicsLayer } = useGraphicsLayer({
    map: view?.map,
    view: view,
    graphicOptions: dottedLineObj,
    addOnLoad: false
  });
  useAsyncEffect(async () => {
    if (id && featureLayer && view && map) {
      // if (id && featureLayer && view && map && !layerView) {
      const layerView = await view.whenLayerView(featureLayer);
      setLayerView(layerView);
      // 1. Extract feature information from the URL (e.g., featureId)
      const featureId = id;

      // 2. Query feature from FeatureLayer
      if (featureId) {
        const query = featureLayer.createQuery();
        query.where = `OBJECTID = ${featureId}`; // Adjust the where clause based on your feature identifier
        const result = await featureLayer.queryFeatures(query);

        if (result.features.length > 0) {
          const feature = result.features[0];
          const match = graphicsLayer?.graphics.getItemAt(0);
          if (match) {
            clearGraphicsLayer(view?.map as any);
            setZoomResult([]);
          } else {
            const outlineGraphic = new Graphic({
              geometry: feature?.geometry as Geometry,
              symbol: dottedLineObj as any
            });
            addGraphicsLayerToMap(view?.map as any, outlineGraphic);
            setZoomResult(result.features);
          }
          await view.goTo(feature?.geometry?.extent?.expand(1.2));
        } else {
          if (zoomResult.length === 0) {
            clearGraphicsLayer(view?.map as any);
            setZoomResult([]);
          }
        }
      }
    }
  }, [featureLayer, view, map, id]);

  return { zoomResult };
};

//
// interface reactiveUtilTemplate {
//     getTarget: () => any;
//     eventName: string;
//     callback: (event: any) => void;
// }
//
// export const useMapFeaturePopup = (
//     results: GraphicHit[],
//     view: InternalMapView | MapView | undefined,
//     featureLayer: FeatureLayer | undefined,
//     template: {
//         title: string;
//         content: { type: string; text: string }[];
//         actions: any[];
//     }
// ) => {
//     const [opportunityId, setOpportunityId] = useState<string | undefined>(undefined);
//
//     const { data: opportunity, isSuccess: opportunityLoaded } = useAimLazyQuery<Opportunity>(
//         opportunityId && parseFloat(opportunityId) !== 0 ? opportunityId : undefined,
//         useLazyGetOpportunityQuery
//     );
//     const { data: transactionTeamDetails, isSuccess: acquisitionLoaded } = useAimLazyQuery<TransactionTeam>(
//         opportunityId && parseFloat(opportunityId) !== 0 ? opportunityId : undefined,
//         useLazyGetTransactionTeamQuery
//     );
//
//     useAsyncEffect(async () => {
//         if (results?.length) {
//             const [{ graphic }] = results;
//             setOpportunityId(graphic?.attributes?.OpportunityID);
//         }
//     }, [results]);
//
//     useAsyncEffect(async () => {
//         if (
//             opportunityId &&
//             opportunityLoaded &&
//             acquisitionLoaded &&
//             featureLayer &&
//             view &&
//             results.length > 0
//         ) {
//             const popup = {
//                 title: template.title,
//                 content: template.content,
//                 actions: [] as any[]
//             };
//             const goToOpportunityAction = {
//                 title: `View Opportunity`,
//                 id: 'go-to-opportunity',
//                 className: 'esri-icon-lightbulb'
//             };
//             const goToAcquisitionAction = {
//                 title: `View Acquisition`,
//                 id: 'go-to-acquisition',
//                 className: 'esri-icon-right-arrow-circled'
//             };
//             if (opportunity && !popup.actions.find((action) => action.id === goToOpportunityAction.id))
//                 popup?.actions?.push(goToOpportunityAction);
//             if (transactionTeamDetails && !popup.actions.find((action) => action.id === goToAcquisitionAction.id))
//                 popup?.actions?.push(goToAcquisitionAction);
//             const [{ mapPoint }] = results;
//             featureLayer.popupEnabled = true;
//             featureLayer.popupTemplate = new PopupTemplate({ ...popup });
//             reactiveUtils.on(
//                 () => view.popup,
//                 'trigger-action',
//                 (event) => {
//                     if (event.action.id === 'go-to-acquisition') {
//                         window.open(`${baseUrl}/acquisition/${opportunityId}`, '_blank');
//                     } else if (event.action.id === 'go-to-opportunity') {
//                         window.open(`${baseUrl}/opportunity/${opportunityId}`, '_blank');
//                     }
//                 }
//             );
//             if (mapPoint) {
//                 await view.openPopup({
//                     location: mapPoint,
//                     fetchFeatures: true,
//                     features: (results as GraphicHit[]).map((i) => i.graphic)
//                 });
//             }
//         }
//     }, [opportunityId, opportunityLoaded, acquisitionLoaded, featureLayer, view, results]);
// };
// export const useRightClickMapFeature = (
//     view: InternalMapView | MapView | undefined,
//     featureLayer: FeatureLayer | undefined,
//     exitCallback: () => void
// ) => {
//     const [layerView, setLayerView] = useState<FeatureLayerView>();
//     const [results, setResults] = useState<GraphicHit[] | undefined>(undefined);
//
//     useAsyncEffect(async () => {
//         if (featureLayer && view && !layerView) {
//             const layerView = await view.whenLayerView(featureLayer);
//             setLayerView(layerView);
//             const handleRightClick = async (response: __esri.HitTestResult) => {
//                 if (response.results.length) {
//                     setResults(response.results as GraphicHit[]);
//                 }
//             };
//             const eventHandler = (event: __esri.ViewClickEvent) => {
//                 view.hitTest(event, { include: featureLayer }).then(async (response) => {
//                     if (event.button === 2) {
//                         await handleRightClick(response);
//                     } else {
//                         exitCallback();
//                     }
//                 });
//             };
//             view.on('click', eventHandler);
//         }
//     }, [featureLayer, view]);
//
//     return results;
// };
//
// export const useSelectMultipleMapFeatures = (
//     view: InternalMapView | MapView | undefined,
//     featureLayer: FeatureLayer | undefined
// ) => {
//     const [selectedResultsArr, setSelectedResultsArr] = useState<Array<GraphicHit[]>>([]);
//     const [layerView, setLayerView] = useState<FeatureLayerView>();
//     const { graphicsLayer, addGraphicsLayerToMap, clearGraphicsLayer } = useGraphicsLayer({
//         map: view?.map,
//         view: view,
//         graphicOptions: dottedLineObj,
//         addOnLoad: false
//     });
//     const selectMultipleHandler = useCallback(
//         (response: __esri.HitTestResult) => {
//             //https://community.esri.com/t5/arcgis-javascript-maps-sdk-questions/select-multiple-features-by-mouse-clicks/td-p/588560
//             if (response.results.length && view) {
//                 const newArr = [...selectedResultsArr, response.results as GraphicHit[]];
//                 // setSelectedResultsArr((oldState) => [...oldState, response.results as GraphicHit[]]);
//                 setSelectedResultsArr(newArr);
//                 const [{ graphic }] = response.results as GraphicHit[];
//                 const matches = graphicsLayer?.graphics;
//                 console.log(matches);
//                 const outlineGraphic = new Graphic({
//                     geometry: graphic.geometry as GeometryProperties,
//                     symbol: dottedLineObj
//                 });
//                 addGraphicsLayerToMap(view.map, outlineGraphic);
//             } else {
//                 if (selectedResultsArr.length !== 0) setSelectedResultsArr([]);
//             }
//         },
//         [selectedResultsArr, view, graphicsLayer]
//     );
//
//     const eventHandler = useCallback(
//         async (event: __esri.ViewPointerDownEvent) => {
//             const response = await view?.hitTest(event, { include: featureLayer }); //.then((response) => {
//             if ((event.native.ctrlKey || event.native.shiftKey) && response) selectMultipleHandler(response);
//             else if (view) clearGraphicsLayer(view?.map);
//         },
//         [selectMultipleHandler, view]
//     );
//     useAsyncEffect(async () => {
//         let viewMoveHandle: IHandle;
//
//         if (featureLayer && view && !layerView) {
//             const layerView = await view.whenLayerView(featureLayer);
//             setLayerView(layerView);
//             viewMoveHandle = view.on('pointer-down', eventHandler);
//         }
//
//         return (async () => viewMoveHandle && viewMoveHandle.remove()) as any;
//     }, [featureLayer, view, eventHandler]);
// };
